import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "./useAuth";

/**
 * Saved views: per-user, per-page named filter presets.
 *
 * The hook is deliberately generic over the filter shape — each list page
 * owns its own `TFilters` and just hands us a serializable snapshot. We
 * persist to localStorage under a namespaced key so different users on
 * the same browser don't clobber each other's presets, and so a page's
 * view list survives hard refreshes without needing a backend round-trip.
 *
 * We intentionally don't sync views across browsers/devices yet — a
 * shipping order-of-operations call. When we add that, the storage
 * contract here (a JSON array of `{id, name, filters}`) maps cleanly to
 * a future `GET/POST /api/v1/saved-views?scope=...` endpoint.
 */
export interface SavedView<TFilters> {
  id: string;
  name: string;
  filters: TFilters;
  /** ISO timestamp — useful for sorting & "last used" UX later. */
  created_at: string;
}

interface UseSavedViewsOptions<TFilters> {
  /**
   * Page-level identifier (e.g. "employees", "complaints"). Scopes the
   * storage key so `Saved view X on employees` doesn't bleed into the
   * complaints list.
   */
  scope: string;
  /**
   * Current live filter state. The hook doesn't own this — pages do —
   * so that the existing filter-URL/debounce wiring keeps working.
   */
  filters: TFilters;
  /**
   * Applied when the user picks a view. Pages translate the snapshot
   * back into their individual filter `useState` setters here.
   */
  onApply: (filters: TFilters) => void;
  /**
   * What "empty" means — used to detect whether filters differ from
   * default (for the "no filters active" case) and to build a
   * `Clear all` action.
   */
  defaultFilters: TFilters;
}

function storageKey(userId: string | null | undefined, scope: string) {
  // Namespaced so (a) it's easy to grep/clear in devtools and (b) a
  // future migration can pattern-match the prefix.
  return `peopleos.views.${userId ?? "anon"}.${scope}`;
}

function readFromStorage<TFilters>(key: string): SavedView<TFilters>[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as SavedView<TFilters>[];
  } catch {
    // Corrupted storage entry — fall back to empty rather than throw.
    // Users shouldn't see their page crash over a bad JSON blob.
    return [];
  }
}

function writeToStorage<TFilters>(
  key: string,
  views: SavedView<TFilters>[]
) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(views));
  } catch {
    // Quota exceeded / private mode — silently drop. The in-memory
    // view list is still correct for this session.
  }
}

function filtersEqual<T>(a: T, b: T): boolean {
  // Stringified-equal is fine for the plain-object filter shapes we
  // pass through (strings, numbers, booleans, small arrays). If a page
  // ever wants to store a Set or Date it will need to normalize before
  // handing us the snapshot.
  return JSON.stringify(a) === JSON.stringify(b);
}

export function useSavedViews<TFilters>({
  scope,
  filters,
  onApply,
  defaultFilters,
}: UseSavedViewsOptions<TFilters>) {
  const { user } = useAuth();
  const key = storageKey(user?.id, scope);

  const [views, setViews] = useState<SavedView<TFilters>[]>(() =>
    readFromStorage<TFilters>(key)
  );
  const [activeViewId, setActiveViewId] = useState<string | null>(null);

  // Re-read when the scope/user changes (e.g. after login), otherwise a
  // stale list persists from the anonymous load.
  useEffect(() => {
    setViews(readFromStorage<TFilters>(key));
    setActiveViewId(null);
  }, [key]);

  const persist = useCallback(
    (next: SavedView<TFilters>[]) => {
      setViews(next);
      writeToStorage(key, next);
    },
    [key]
  );

  const saveView = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return null;
      const view: SavedView<TFilters> = {
        id: `view-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: trimmed,
        filters,
        created_at: new Date().toISOString(),
      };
      persist([...views, view]);
      setActiveViewId(view.id);
      return view;
    },
    [filters, persist, views]
  );

  const applyView = useCallback(
    (id: string) => {
      const v = views.find((x) => x.id === id);
      if (!v) return;
      setActiveViewId(id);
      onApply(v.filters);
    },
    [onApply, views]
  );

  const deleteView = useCallback(
    (id: string) => {
      persist(views.filter((v) => v.id !== id));
      if (activeViewId === id) setActiveViewId(null);
    },
    [activeViewId, persist, views]
  );

  const renameView = useCallback(
    (id: string, name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      persist(
        views.map((v) => (v.id === id ? { ...v, name: trimmed } : v))
      );
    },
    [persist, views]
  );

  /**
   * Overwrite the currently-active view with the live filters — for
   * the "you tweaked a view, want to pin the changes?" flow.
   */
  const updateActiveView = useCallback(() => {
    if (!activeViewId) return;
    persist(
      views.map((v) =>
        v.id === activeViewId ? { ...v, filters } : v
      )
    );
  }, [activeViewId, filters, persist, views]);

  const activeView = useMemo(
    () => views.find((v) => v.id === activeViewId) ?? null,
    [activeViewId, views]
  );

  const isDefault = useMemo(
    () => filtersEqual(filters, defaultFilters),
    [filters, defaultFilters]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!activeView) return false;
    return !filtersEqual(filters, activeView.filters);
  }, [activeView, filters]);

  // If the live filters drift away from the active view, drop the
  // "active" pointer so the UI doesn't falsely claim the user is
  // looking at a saved view.
  useEffect(() => {
    if (activeView && hasUnsavedChanges) {
      // We keep `activeViewId` so `updateActiveView()` is meaningful,
      // but surface `hasUnsavedChanges` to the menu for the "modified"
      // indicator.
    }
  }, [activeView, hasUnsavedChanges]);

  return {
    views,
    activeView,
    activeViewId,
    saveView,
    applyView,
    deleteView,
    renameView,
    updateActiveView,
    isDefault,
    hasUnsavedChanges,
    clear: () => {
      setActiveViewId(null);
      onApply(defaultFilters);
    },
  };
}
